import { Link } from 'react-router-dom';

import { Instagram, Facebook, Youtube, AtSign, Mail, MapPin, Phone, ArrowRight } from 'lucide-react';
import WhatsAppIcon from 'components/ui/WhatsAppIcon';
import { BRAND, NAVIGATION, SOCIAL_LINKS } from 'lib/constants';
import { getWhatsAppUrl } from 'lib/whatsapp';
import logoImg from '../../images/logo/MORA.webp';

const iconMap = {
  Instagram,
  Facebook,
  Youtube,
  AtSign,
  Mail,
};

export default function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative bg-[#1A0F08] text-Mora-cream/90 overflow-hidden border-t border-Mora-caramel/20">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-Mora-caramel/5 rounded-full blur-3xl pointer-events-none transform translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-Mora-brown/20 rounded-full blur-3xl pointer-events-none transform -translate-x-1/2 translate-y-1/2" />

      {/* Main Footer */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 pt-16 sm:pt-20 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8 mb-10">
          {/* Brand */}
          <div className="md:col-span-2 lg:col-span-5 pr-0 lg:pr-10">
            <Link to="/" className="inline-block z-10 transition-transform hover:scale-[1.02] active:scale-[0.98]">
              <div className="relative w-auto h-12 sm:h-14 mb-6 transition-all duration-300">
                <img
                  src={logoImg}
                  alt="Mora"
                  width={240}
                  height={64}
                  className="w-auto h-full object-contain brightness-0 invert opacity-95"
                />
              </div>
            </Link>
            <p className="text-sm font-semibold text-Mora-caramel tracking-[0.2em] uppercase mb-4">
              {BRAND.tagline}
            </p>
            <p className="text-base text-Mora-cream/70 leading-relaxed max-w-md mb-8">
              MORA ENTERPRISE brings you premium Bengaluru Sopari with a focus on quality, careful selection, and traditional taste. Our commitment is to deliver consistent quality and a distinctive identity in every pack.  </p>

            {/* Social Links */}
            <div className="flex items-center gap-3">
              {SOCIAL_LINKS.map((social) => {
                const Icon = iconMap[social.icon] || Mail;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-Mora-cream/5 text-Mora-cream/80 hover:bg-Mora-caramel hover:text-white hover:-translate-y-1 transition-all duration-300 border border-Mora-cream/10 hover:border-Mora-caramel hover:shadow-lg hover:shadow-Mora-caramel/20"
                    aria-label={social.label}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Navigation */}
          <div className="md:col-span-1 lg:col-span-2 lg:col-start-7">
            <h3 className="font-heading text-lg font-bold text-white mb-6 flex items-center gap-2">
              Explore
              <div className="h-px bg-Mora-caramel/30 flex-grow ml-4"></div>
            </h3>
            <ul className="space-y-4">
              {NAVIGATION.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="group flex items-center text-Mora-cream/70 hover:text-Mora-caramel transition-colors duration-200"
                  >
                    <ArrowRight className="w-4 h-4 mr-2 opacity-0 -ml-6 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300" />
                    <span className="group-hover:translate-x-1 transition-transform duration-300">
                      {link.label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-1 lg:col-span-4">
            <h3 className="font-heading text-lg font-bold text-white mb-6 flex items-center gap-2">
              Contact
              <div className="h-px bg-Mora-caramel/30 flex-grow ml-4"></div>
            </h3>
            <ul className="space-y-5">
              <li>
                <a
                  href={BRAND.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 text-Mora-cream/70 hover:text-white transition-colors group"
                >
                  <div className="w-8 h-8 rounded-full bg-Mora-cream/5 flex items-center justify-center flex-shrink-0 group-hover:bg-Mora-caramel group-hover:text-white transition-colors">
                    <MapPin className="w-4 h-4 text-Mora-caramel group-hover:text-white" />
                  </div>
                  <span className="leading-relaxed text-sm pt-1.5 group-hover:text-Mora-cream transition-colors">
                    {BRAND.address}
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${BRAND.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 text-Mora-cream/70 hover:text-white transition-colors group"
                >
                  <div className="w-8 h-8 rounded-full bg-Mora-cream/5 flex items-center justify-center flex-shrink-0 group-hover:bg-[#25D366] group-hover:text-white transition-colors">
                    <WhatsAppIcon className="w-4 h-4 text-Mora-caramel group-hover:text-white" />
                  </div>
                  <span className="text-sm group-hover:text-Mora-cream transition-colors">
                    WhatsApp: <span className="font-semibold text-white tracking-wide block sm:inline">{BRAND.phoneDisplay}</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${BRAND.phone}`}
                  className="flex items-center gap-4 text-Mora-cream/70 hover:text-white transition-colors group"
                >
                  <div className="w-8 h-8 rounded-full bg-Mora-cream/5 flex items-center justify-center flex-shrink-0 group-hover:bg-Mora-caramel group-hover:text-white transition-colors">
                    <Phone className="w-4 h-4 text-Mora-caramel group-hover:text-white" />
                  </div>
                  <span className="text-sm group-hover:text-Mora-cream transition-colors">
                    Call: <span className="font-semibold text-white tracking-wide block sm:inline">{BRAND.phoneDisplay}</span>
                  </span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${BRAND.email}`}
                  className="flex items-center gap-4 text-Mora-cream/70 hover:text-white transition-colors group"
                >
                  <div className="w-8 h-8 rounded-full bg-Mora-cream/5 flex items-center justify-center flex-shrink-0 group-hover:bg-Mora-caramel group-hover:text-white transition-colors">
                    <Mail className="w-4 h-4 text-Mora-caramel group-hover:text-white" />
                  </div>
                  <span className="text-sm break-all sm:break-normal group-hover:text-Mora-cream transition-colors">{BRAND.email}</span>
                </a>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-4 border-t border-Mora-cream/10 flex flex-col sm:flex-row items-center justify-between gap-1">
          <p className="text-xs text-Mora-cream/50 font-body">
            © {currentYear} Mora. All rights reserved.
          </p>
          <p className="text-xs text-Mora-cream/40 font-body">
            Powered by{' '}
            <a
              href="https://liontouchwebpark.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-Mora-caramel hover:text-white transition-colors font-medium relative group inline-block"
            >
              Liontouch Webpark
              <span className="absolute -bottom-1 left-0 w-0 h-px bg-Mora-caramel group-hover:w-full transition-all duration-300"></span>
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
